import { gifPaginator, GifsResult } from '@giphy/js-fetch-api'
import produce from 'immer'
import { IGif } from '@giphy/js-types'
import { useContext, useRef, useState, useEffect } from 'react'
import GifContext from 'shared/components/context/gif'
import UserContext from 'shared/components/context/user'

export function useGifPaginator(
    fetchGifs: (offset: number) => Promise<GifsResult>
): [IGif[], () => Promise<IGif[]>, boolean] {
    // all the gifs that the fetchGifs has returned
    const [gifs, setGifs] = useState<IGif[]>([])
    const { isLoggedIn } = useContext(UserContext)
    // when we fetch and there are no more gifs
    const [isDoneFetching, setDoneFetching] = useState<boolean>(false)
    // we put all the gis in the gif context for other components
    const { receivedGifs, getGifById, gifs: existingGifs } = useContext(GifContext)
    // paginator for gifs or videos
    const paginator = useRef(gifPaginator(fetchGifs))

    // ref so doFetch can check gifs state
    const check = (updateGifs: IGif[]) => gifs.length === updateGifs.length
    const checkIsDone = useRef(check)
    checkIsDone.current = check

    useEffect(() => {
        // update any gifs that may have been edited
        if (isLoggedIn) {
            const updatedGifs = produce(gifs, draftGifs => {
                draftGifs.forEach((gif, index) => {
                    const existingGif = getGifById(gif.id)
                    if (existingGif) {
                        draftGifs[index] = existingGif
                    }
                })
            })
            if (updatedGifs !== gifs) {
                setGifs(updatedGifs)
            }
        }
    }, [existingGifs])

    // make the request
    const doFetch = async () => {
        // put gifs in our local state here
        // and also in the context
        const addGifs = (gifs: IGif[]) => {
            setGifs(gifs)
            receivedGifs({ gifs })
        }
        // use the paginator to fetch gifs
        const updateGifs = await paginator.current()
        if (isLoggedIn) {
            // the paginator's source of truth is fetches,
            // but we may have updated on the client
            updateGifs.forEach((gif, index) => {
                const existingGif = getGifById(gif.id)
                if (existingGif) {
                    updateGifs[index] = existingGif
                }
            })
        }
        // if this is true we've reached the end
        setDoneFetching(checkIsDone.current(updateGifs))
        //
        addGifs(updateGifs)
        // return just in case it makes it easier for
        // a consuming component, but it probably doesn't it
        return updateGifs
    }
    return [gifs, doFetch, isDoneFetching]
}
